import {
	Alert,
	Box,
	Button,
	Flex,
	Stack,
	Text,
	ThemeIcon,
	Title,
	useMantineTheme,
} from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	witnessEmail?: string;
	inviteFailed?: boolean;
	isResending?: boolean;
	onResend: () => void;
	onDone: () => void;
}

const NEXT_STEPS = [
	{
		titleKey: 'document_workflow.witness.next_step_1_title',
		descKey: 'document_workflow.witness.next_step_1_desc',
	},
	{
		titleKey: 'document_workflow.witness.next_step_2_title',
		descKey: 'document_workflow.witness.next_step_2_desc',
	},
	{
		titleKey: 'document_workflow.witness.next_step_3_title',
		descKey: 'document_workflow.witness.next_step_3_desc',
	},
];

export const WitnessSuccessStep = ({
	witnessEmail,
	inviteFailed = false,
	isResending = false,
	onResend,
	onDone,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const theme = useMantineTheme();
	const showWitnessNextSteps = !!witnessEmail && !inviteFailed;

	return (
		<Flex
			direction="column"
			h="100%"
			p="1rem"
			gap="lg"
			data-testid={TEST_ELEMENT_IDS.INVESTOR_WITNESS_SUCCESS_CONFIRMATION}
		>
			{inviteFailed ? (
				<Alert
					color="red.9"
					variant="light"
					icon={<Icon variant="warning" size="md" />}
					title={tInvestor('document_workflow.witness.invite_failed')}
				>
					<Button
						data-testid="witness-invite-retry"
						variant="outline"
						loading={isResending}
						onClick={onResend}
						leftSection={<Icon variant="mail" size="md" />}
					>
						{tInvestor('document_workflow.witness.resend')}
					</Button>
				</Alert>
			) : (
				<Box
					p="md"
					bg="green.0"
					sx={(t) => ({
						display: 'flex',
						gap: t.spacing.sm,
						alignItems: 'flex-start',
						borderRadius: t.radius.md,
						border: `1px solid ${t.colors.teal[9]}`,
					})}
				>
					<ThemeIcon
						radius="xl"
						size="lg"
						sx={(t) => ({ backgroundColor: t.colors.teal[8] })}
					>
						<Icon variant="checkmark" size="lg" color={theme.white} />
					</ThemeIcon>
					<div style={{ minWidth: 0 }}>
						<Text fw={700} c="teal.9">
							{tInvestor('document_workflow.witness.success_title')}
						</Text>
						{witnessEmail && (
							<Text size="0.75rem" c="teal.9" style={{ overflowWrap: 'anywhere' }}>
								{tInvestor('document_workflow.witness.invite_sent_to')}{' '}
								<Text span fw={700}>
									{witnessEmail}
								</Text>
							</Text>
						)}
					</div>
				</Box>
			)}

			{showWitnessNextSteps && (
				<div>
					<Title order={5} mb="sm">
						{tInvestor('document_workflow.witness.what_happens_next')}
					</Title>
					<Stack gap="sm">
						{NEXT_STEPS.map((step, index) => (
							<Flex key={step.titleKey} gap="sm" align="flex-start">
								<ThemeIcon variant="outline" color="gray" radius="xl" size="md">
									<Text size="md" color="gray.9">
										{index + 1}
									</Text>
								</ThemeIcon>
								<div>
									<Text fw={600}>{tInvestor(step.titleKey)}</Text>
									<Text size="0.75rem" c="gray.9">
										{tInvestor(step.descKey)}
									</Text>
								</div>
							</Flex>
						))}
					</Stack>
				</div>
			)}

			<Button fullWidth mt="auto" onClick={onDone}>
				{tInvestor('document_workflow.witness.back_to_documents')}
			</Button>
		</Flex>
	);
};
